import { useTranslation } from "react-i18next";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { useLeaveBoard, useLeaveConfigure, type LeaveBoard } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

/** Leave configuration read back as findings (HRX-04).
 *
 * A screen of zeros would be useless here: the point is not that there are no leave policies, it is that
 * entitlement is therefore typed in by hand — 354 allocations, none of them from a policy, granting every
 * employee the same 24 days whatever their grade and whenever they joined. Each check states what is wrong and
 * offers the one act that fixes it, because each of those acts changes real data and none should happen as a
 * side effect of opening a page. */

/** Which action closes which finding. `null` means the finding is reported but not actionable from here. */
const ACTION: Record<string, string | null> = {
  policy: "configure_policies",
  accrual: "configure_types",
  carry_forward: "open_next_period",
  encashment: "configure_types",
  block_lists: "create_block_list",
  comp_off: null,
};

export default function Policy({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useLeaveBoard(company || undefined);
  const configure = useLeaveConfigure();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-leave-policy>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-leave-strip>
        <Stat label={t("lv.checksFailing")} value={failing.length} cls={failing.length ? "text-status-amber" : "text-status-green"}
          hint={t("lv.ofChecks", { n: d.state.length })} />
        <Stat label={t("lv.allocations")} value={d.allocations.total} hint={t("lv.forPeople", { n: d.allocations.people })} />
        <Stat label={t("lv.handWritten")} value={d.allocations.hand_written}
          cls={d.allocations.hand_written ? "text-status-amber" : ""} hint={t("lv.handWrittenHint")} />
        <Stat label={t("lv.policies")} value={d.policies.length} hint={t("lv.gradesN", { n: d.grades.length })} />
      </div>

      {d.misleading_frequency.length > 0 && (
        <div className="rounded border border-status-red/40 bg-red-50 px-4 py-3 text-sm text-status-red" data-leave-misleading>
          {t("lv.misleading", { n: d.misleading_frequency.length, types: d.misleading_frequency.slice(0, 3).join(", ") })}
        </div>
      )}

      <SectionCard title={t("lv.checks")} aside={d.period ? `${d.period.from_date} → ${d.period.to_date}` : t("lv.noPeriod")}>
        <ul>
          {d.state.map((s) => (
            <li key={s.key} className="flex flex-wrap items-start justify-between gap-3 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
              data-leave-check={s.key} data-leave-check-ok={s.ok ? "1" : "0"}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${s.ok ? "bg-status-green" : "bg-status-amber"}`} />
                  <span className="font-semibold">{t(`lv.check.${s.key}.title`)}</span>
                </div>
                <p className="mt-1 text-sm text-brand-grey">{t(`lv.check.${s.key}.${s.ok ? "ok" : "bad"}`, s.facts) as string}</p>
              </div>
              {!s.ok && ACTION[s.key] && d.permissions.configure && (
                <button onClick={() => configure.mutate({ action: ACTION[s.key]! })} disabled={configure.isPending}
                  data-leave-fix={s.key}
                  className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                  {t(`lv.act.${ACTION[s.key]}`)}
                </button>
              )}
            </li>
          ))}
        </ul>
      </SectionCard>

      {configure.isError && <ErrorBox error={configure.error} />}
      {configure.isSuccess && (
        <div className="rounded border border-status-green/40 bg-green-50 px-4 py-2 text-sm text-status-green" data-leave-configured>
          {t("lv.applied")}
        </div>
      )}

      <Indicative d={d} />

      <SectionCard title={t("lv.types")} aside={t("lv.typesN", { n: d.types.length })}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("lv.type")}</th>
                <th className="px-2 py-2 text-right">{t("lv.max")}</th>
                <th className="px-2 py-2">{t("lv.accrues")}</th>
                <th className="px-2 py-2">{t("lv.carryForward")}</th>
                <th className="px-4 py-2">{t("lv.encashment")}</th>
              </tr>
            </thead>
            <tbody>
              {d.types.map((ty) => (
                <tr key={ty.name} className="border-t border-brand-grey-light" data-leave-type={ty.name}>
                  <td className="px-4 py-2 font-medium">
                    {ty.name}
                    {ty.is_lwp ? <span className="ml-1 text-[10px] text-brand-grey">(LWP)</span> : null}
                    {ty.is_compensatory ? <span className="ml-1 text-[10px] text-brand-grey">(comp-off)</span> : null}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-brand-grey">{ty.max_leaves_allowed || "–"}</td>
                  <td className="px-2 py-2">
                    {ty.is_earned_leave
                      ? <span className="text-status-green">{ty.earned_leave_frequency}</span>
                      : ty.earned_leave_frequency
                        ? <span className="text-status-red">{t("lv.frequencyWithoutAccrual")}</span>
                        : <span className="text-brand-grey">{t("lv.upfront")}</span>}
                  </td>
                  <td className="px-2 py-2 text-brand-grey">
                    {ty.is_carry_forward
                      ? (ty.maximum_carry_forwarded_leaves
                        ? t("lv.cappedAt", { n: ty.maximum_carry_forwarded_leaves })
                        : <span className="text-status-amber">{t("lv.declaredNoCap")}</span>)
                      : "–"}
                  </td>
                  <td className="px-4 py-2 text-brand-grey">
                    {ty.allow_encashment
                      ? (ty.max_encashable_leaves
                        ? t("lv.upToDays", { n: ty.max_encashable_leaves, keep: ty.non_encashable_leaves })
                        : <span className="text-status-amber">{t("lv.allowedNoMax")}</span>)
                      : "–"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}

function Indicative({ d }: { d: LeaveBoard }) {
  const { t } = useTranslation();
  const grades = (d.indicative.grade_days ?? {}) as Record<string, number>;
  return (
    <Card className="p-4" data-leave-indicative>
      <h3 className="font-heading text-base font-semibold">{t("lv.indicative")}</h3>
      <p className="mt-1 max-w-3xl text-sm text-brand-grey">{t("lv.indicativeHint")}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
        {Object.entries(grades).map(([grade, days]) => (
          <span key={grade} className="rounded border border-brand-grey-light px-2 py-1">
            <span className="font-semibold">{grade}</span> · {t("lv.daysN", { n: days })}
          </span>
        ))}
        <span className="rounded border border-brand-grey-light px-2 py-1">
          <span className="font-semibold">{t("lv.ungraded")}</span> · {t("lv.daysN", { n: d.indicative.default_days })}
        </span>
        <span className="rounded border border-brand-grey-light px-2 py-1">
          {t("lv.seniorityRule", { step: d.indicative.per_step, years: d.indicative.per_years, cap: d.indicative.cap })}
        </span>
      </div>
    </Card>
  );
}
